import { Location } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  OnChanges,
  ChangeDetectorRef,
} from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Params } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { ESalesOrderAction } from '@app/core/_constant/sales-orders.constants';
import { ShipOrdersComponent } from '@app/modules/shipping/ship-orders/ship-orders.component';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';
import { AddressTransformationType } from 'v2/modules/shared/pipes/address-transformation.pipe';

import { ICustomFieldValue } from '../../../custom-field/interfaces/custom-field.interface';
import { EInvoicePaymentStatus } from '../../../invoice/interfaces/invoice.interface';
import { PicklistConfig } from '../../../picklist/config/picklist.config';
import {
  PicklistStatus,
  EPicklistOrderSource,
} from '../../../picklist/interfaces/picklist.interface';
import { ISalesOrderActionsPayload } from '../../../sales-order/interfaces/sales-order.interface';
import { DialogConfig } from '../../../shared';
import { OrderPostedApiService } from '../../../shared/services/api/order/order-posted-api.service';
import { trackById } from '../../../shared/util';
import {
  IPickingItem,
  ESalesOrderPostStatus,
} from '../../interfaces/order-sales.interface';
import { FulfillmentOrdersGridActionService } from '../order-fulfillments-grid/services/fulfillment-orders-grid-action.service';
import { OrderShipFormV2Component } from '../order-ship-form-v2/order-ship-form-v2.component';

@Component({
  selector: 'lum-order-posted',
  templateUrl: './order-posted.component.html',
  styleUrls: ['./order-posted.component.scss'],
})
export class OrderPostedComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  latestAdjustmentEntry: string;

  @Input()
  latestSalesOrderUpdate: string;

  @Input()
  salesOrderId: number | null = null;

  @Input()
  settings: any;

  @Output()
  createAdjustmentEntryEvent = new EventEmitter<number>();

  @Output()
  createShipmentEvent = new EventEmitter();

  selectedRowCount$ = new BehaviorSubject<number>(0);

  @Output()
  selectedRowIds$ = new BehaviorSubject<number[]>([]);

  @Output()
  selectedfulfillmentConfirmationValues$ = new BehaviorSubject<any[]>([]);

  @Output()
  selectedRows$ = new BehaviorSubject<any[]>([]);

  @Output()
  clearGridTrigger = new EventEmitter<void>();

  @Output()
  refetchGridDataTrigger = new EventEmitter<void>();

  @Output()
  salesOrderActionEvent = new EventEmitter<{
    action: string;
    salesOrder: any;
    payload?: ISalesOrderActionsPayload;
  }>();

  @Output()
  closeModalEvent = new EventEmitter<boolean>();

  salesOrder: any = {}; // TODO add salesOrder interface
  pickingItems: IPickingItem[] = [];
  relations: string = [
    'clientInvoice',
    'clientInvoice:id,client_id,payment_status,paid_amount,net_total_amount',
    'clientInvoice.client:id,business_name',
    'purchaseOrder',
    'orderItems.orderedUom:id,name',
    'shipstationShipments',
    'picklist.updatedByUser:id,firstname,lastname',
    'items.rfq:id,internal_sku,type',
    'items.kit:id,sku',
    'items.orderedUom:id,name',
    'shipmentTrackingNumbers',
    'trackingNumbers',
    'picklistOrder.verifiedBy:id,firstname,lastname',
    'tags',
    'warehouseGroup',
    'customFieldValues:value_string,custom_field',
    'salesOrderShipments',
    'salesOrderShipments.warehouseGroup',
    'salesOrderShipments.destinationOms',
    'salesOrderShipments.destinationOmsStore',
    'clientInvoices',
    'clientInvoices.clientPurchaseOrderType',
    'clientInvoices.customer',
    'clientInvoices.salesChannel',
    'channelIntegrationTransactions',
    'channel.pushOrderIntegrationMapping.appIntegrationAccount',
  ].join('|');

  loading: boolean = false;
  isInModal: boolean = false;
  currentActiveTab: number = 0;
  addressTransformationType = AddressTransformationType;

  readonly ESalesOrderAction = ESalesOrderAction;
  readonly eInvoicePaymentStatus = EInvoicePaymentStatus;
  readonly eSalesOrderPostStatus = ESalesOrderPostStatus;
  readonly picklistStatusConstant = PicklistConfig.picklistStatus;
  readonly trackById = trackById;

  private onDestroy$ = new Subject();

  constructor(
    private _feature: FeatureToggleService,
    private cd: ChangeDetectorRef,
    private orderPostedApiService: OrderPostedApiService,
    private route: ActivatedRoute,
    private location: Location,
    private modal: NgbModal,
    private dialog: MatDialog,
    private fulfillmentOrdersGridActionService: FulfillmentOrdersGridActionService,
  ) {}

  ngOnInit(): void {
    this.getRouterParams();
    this.getSalesOrder();
    this.getPickingItems();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['latestAdjustmentEntry']?.currentValue) {
      this.getPickingItems();
    }
    if (changes['latestSalesOrderUpdate']?.currentValue) {
      this.getSalesOrder();
    }
  }

  goBack(): void {
    this.location.back();
  }

  closeModal(): void {
    this.closeModalEvent.emit(true);
  }

  onTriggerActions(action: string, payload: ISalesOrderActionsPayload = {}): void {
    this.salesOrderActionEvent.emit({
      action,
      salesOrder: this.salesOrder,
      payload: payload,
    });
  }

  onOpenShipmentForm(): void {
    this.createShipmentEvent.emit();
  }

  onSelectedRowCountEvent(value: number): void {
    this.selectedRowCount$.next(value);
  }

  onSelectedfulfillmentConfirmationValuesEvent(value: any): void {
    this.selectedfulfillmentConfirmationValues$.next(value);
  }

  onSelectedRowIdsEvent(value: number[]): void {
    this.selectedRowIds$.next(value);
  }

  onSelectedRowsEvent(value: any[]): void {
    this.selectedRows$.next(value);
  }

  private getRouterParams(): void {
    this.route.params
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((params: Params) => {
        const id = params['id'];
        if (id) {
          this.salesOrderId = id;
        } else {
          this.isInModal = true;
        }
      });
  }

  private getSalesOrder(): void {
    this.loading = true;

    this.orderPostedApiService
      .getById({
        id: this.salesOrderId ?? 0,
        params: {
          relations: this.relations,
          relationsWithColumns: 'true',
          excludeIgnoredOrderItems: 'true',
        },
      })
      .pipe(
        takeUntil(this.onDestroy$),
        tap({
          next: () => {
            this.loading = false;
          },
          error: () => {
            this.loading = false;
          },
        }),
      )
      .subscribe({
        next: (salesOrder) => {
          if (salesOrder) {
            this.salesOrder = salesOrder;
          }
          this.cd.detectChanges();
        },
      });
  }

  private getPickingItems(): void {
    this.orderPostedApiService
      .getOrderPostedPickingItems(this.salesOrderId ?? 0)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          const { pickingItems } = response;
          this.pickingItems = (pickingItems || []).map(
            (pickingItem: IPickingItem) => {
              return {
                ...pickingItem,
                fulfilled:
                  pickingItem.availableQuantity >= pickingItem.orderedQuantity,
              };
            },
          );
          this.cd.detectChanges();
        },
      });
  }

  createFulfillmentOrder(): void {
    const salesOrderData: any[] = [];
    salesOrderData.push(this.salesOrder);

    if (this._feature.isActive('red_DV-2840_partial_fulfillment')) {
      this.dialog
        .open(OrderShipFormV2Component, {
          data: {
            title: 'Fulfill Orders',
            salesOrders: salesOrderData,
            ftEdiIntegrationEnabled: this._feature.isActive(
              'red_DV-2272_edi_integration',
            ),
          },
          width: DialogConfig.sizes['xx-large'],
        })
        .afterClosed()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((response: boolean) => {
          this.fulfillmentOrdersGridActionService.refreshGrid();
        });

      return;
    }
    const modalRef = this.modal.open(ShipOrdersComponent, {
      size: 'lg',
      windowClass: 'ship-orders-modal',
    });
    modalRef.componentInstance.salesOrders = salesOrderData;
    modalRef.result.then((hasUpdates: boolean) => {
      if (hasUpdates) {
        this.fulfillmentOrdersGridActionService.refreshGrid();
      }
    });
  }

  onCreateInvoiceFromFulfillmentOrderEvent(updated: boolean = false): void {
    if (updated) {
      this.getSalesOrder();
    }
  }

  get showCreatePicklist(): boolean {
    return !this.salesOrder.picklist && this.salesOrder.isPosted;
  }

  get showVisualPicking(): boolean {
    return this.settings.pickAndPackEnabled && this.isPicking;
  }

  get showPacking(): boolean {
    return this.settings.pickAndPackEnabled && this.isPacking;
  }

  get showPrintShippingLabels(): boolean {
    return (
      this.salesOrder.source === EPicklistOrderSource.SHIPSTATION &&
      this.salesOrder.isPosted &&
      !this.hasShippingLabel
    );
  }

  get isPicking(): boolean {
    return this.salesOrder?.picklist?.id > 0
      ? this.salesOrder?.picklist?.status === PicklistStatus.CREATED ||
          this.salesOrder?.picklist?.status === PicklistStatus.PICKING
      : false;
  }

  get isPacking(): boolean {
    return this.salesOrder?.picklist?.id > 0
      ? this.salesOrder?.picklist?.status === PicklistStatus.PICKED ||
          this.salesOrder?.picklist?.status === PicklistStatus.PACKED
      : false;
  }

  get hasShippingLabel(): boolean {
    return (this.salesOrder?.shipstationShipments || []).length > 0;
  }

  get salesOrderShippingAddress(): string {
    return this.salesOrder.shipTo
      ? [
          this.salesOrder.shipTo?.street1 || '',
          this.salesOrder.shipTo?.street2 || '',
          this.salesOrder.shipTo?.street3 || '',
          this.salesOrder.shipTo?.city || '',
          this.salesOrder.shipTo?.state || '',
          this.salesOrder.shipTo?.postalCode || '',
          this.salesOrder.shipTo?.country || '',
        ]
          .filter(Boolean)
          .join(', ')
      : '';
  }

  get salesOrderShipmentTrackingNumbers(): string {
    const manualTrackingNumbers =
      this.salesOrder.trackingNumbers?.map((row: any) => row.trackingNumber) || [];

    const shipmentTrackingNumbers =
      this.salesOrder.shipmentTrackingNumbers?.map(
        (row: any) => row.trackingNumber,
      ) || [];

    return [...manualTrackingNumbers, ...shipmentTrackingNumbers].join(', ');
  }

  get pickPackUpdateBy(): string {
    return this.salesOrder?.picklist
      ? `${this.salesOrder?.picklist?.updatedByUser?.firstname || ''} ${
          this.salesOrder?.picklist?.updatedByUser?.lastname || ''
        }`
      : '';
  }

  get picklistVerifiedBy(): string {
    return this.salesOrder?.picklistOrder
      ? `${this.salesOrder?.picklistOrder?.verifiedBy?.firstName || ''} ${
          this.salesOrder?.picklistOrder?.verifiedBy?.fullName || ''
        }`
      : '';
  }

  get showShippingLabelIndicator(): boolean {
    return (this.salesOrder?.shipstationShipments || []) && this.hasShippingLabel;
  }

  get customFieldValues(): ICustomFieldValue[] {
    return this.salesOrder?.customFieldValues || [];
  }

  get hasCustomFieldValues(): boolean {
    return this.customFieldValues.length > 0;
  }

  get firstClientInvoice(): any {
    return this.salesOrder?.clientInvoices?.[0];
  }

  get invoiceClientShippingAddress(): string {
    return this.salesOrderShippingAddress?.trim() || '';
  }

  get invoiceClientShippingCompany(): string {
    return this.salesOrder.shipTo?.company?.trim() || '';
  }

  get invoiceClientShippingName(): string {
    return this.salesOrder.shipTo?.name?.trim() || '';
  }
}
